import { IBaseAnalyticsCombined } from "@/store/analyticsDataStore";
import { SetStateAction, useEffect, useState } from "react";
import SelectAllCheckBox from "../SelectAllCheckBox";
import { SankeyDataFilter } from "./IndexFlowAnalysis";
import { returnKeysToMapGeneric } from "../analyticsHelperFunctions";

export const sankeyKeys = [
  "instrumentType",
  "currency",
  "structureType",
  "DV01Buckets",
  "issueCountry",
  "strategyType",
  "RPCustomer",
] as (keyof IBaseAnalyticsCombined)[];

const MultiFilterSankey = ({
  data,
  dataFilters,
  setDataFilters,
}: {
  data: any;
  dataFilters: SankeyDataFilter;
  setDataFilters: React.Dispatch<SetStateAction<SankeyDataFilter>>;
}) => {
  const [keysToMap, setKeysToMap] = useState(
    returnKeysToMapGeneric(data, sankeyKeys),
  );

  useEffect(() => {
    setKeysToMap(returnKeysToMapGeneric(data, sankeyKeys));
  }, [data]);

  useEffect(() => {
    if (dataFilters && keysToMap) {
      setDataFilters({
        ...dataFilters,
        selectedCurrencies: keysToMap.currencyKeys,
        selectedInstruments: keysToMap.instrumentTypeKeys,
        selectedStructure: keysToMap.structureTypeKeys,
        selectedBuckets: keysToMap.DV01BucketsKeys,
        selectedIssueCountry: keysToMap.issueCountryKeys,
        selectedStrategy: keysToMap.strategyTypeKeys,
        selectedCustomers: keysToMap.RPCustomerKeys,
      });
    }
  }, []);

  type StringArrayKeys<T> = {
    [K in keyof T]: T[K] extends string[] ? K : never;
  }[keyof T];

  type FilterableKeys = StringArrayKeys<SankeyDataFilter>;

  const handleCheckboxChange = <
    K extends FilterableKeys, // key of the object
  >(
    dataFilters: SankeyDataFilter,
    filterValue: string,
    dataFilterField: K,
  ) => {
    const currentState = dataFilters[dataFilterField];

    let newData = [];
    if (currentState.includes(filterValue)) {
      // Remove if already selected
      newData = currentState.filter((c: string) => c !== filterValue);
    } else {
      // Add if not selected
      newData = [...currentState, filterValue];
    }
    return { ...dataFilters, [dataFilterField]: newData };
  };

  interface IcheckBoxOptions {
    heading: string;
    dataFilterField: keyof SankeyDataFilter;
    keysToMapField: string;
  }

  const topCheckBoxOptions: IcheckBoxOptions[] = [
    {
      heading: "Currency",
      dataFilterField: "selectedCurrencies",
      keysToMapField: "currencyKeys",
    },
    {
      heading: "Instrument",
      dataFilterField: "selectedInstruments",
      keysToMapField: "instrumentTypeKeys",
    },
  ];

  const secondCheckBoxOptions: IcheckBoxOptions[] = [
    {
      heading: "Structures",
      dataFilterField: "selectedStructure",
      keysToMapField: "structureTypeKeys",
    },
    {
      heading: "DV01 Buckets",
      dataFilterField: "selectedBuckets",
      keysToMapField: "DV01BucketsKeys",
    },
    {
      heading: "RP Customer",
      dataFilterField: "selectedCustomers",
      keysToMapField: "RPCustomerKeys",
    },
    {
      heading: "Structure Type",
      dataFilterField: "selectedStructure",
      keysToMapField: "structureTypeKeys",
    },
    {
      heading: "Strategy Type",
      dataFilterField: "selectedStrategy",
      keysToMapField: "strategyTypeKeys",
    },
    {
      heading: "Issue Country",
      dataFilterField: "selectedIssueCountry",
      keysToMapField: "issueCountryKeys",
    },
  ];

  const [expandedIndex, setExpandedIndex] = useState(null); // Tracks which item is expanded

  const toggleItem = (index: any) => {
    setExpandedIndex(expandedIndex === index ? null : index); // Toggle or collapse if already expanded
  };

  return (
    <div className="flex h-[calc(100%-100px)]">
      {keysToMap ? (
        <div className="cubeCustomAnalyticsScroll flex h-full w-full flex-col overflow-y-auto ">
          {/* FIRST ROW */}

          <div id="TopRowFilters" className="flex h-fit justify-between ">
            {/*  */}
            {topCheckBoxOptions.map((checkOption, index) => (
              <div key={index} className="flex h-full w-full flex-col pl-2">
                <div>{checkOption.heading}:</div>
                <SelectAllCheckBox
                  dataFilters={dataFilters}
                  setDataFilters={setDataFilters}
                  dataFilterField={checkOption.dataFilterField}
                  keysToMap={keysToMap}
                  keysToMapField={checkOption.keysToMapField}
                />
                {keysToMap[checkOption.keysToMapField].map((field, index) => (
                  <div className=" text-xs" key={index}>
                    <input
                      type="checkbox"
                      className=" accent-blue-500"
                      checked={
                        dataFilters && dataFilters[checkOption.dataFilterField]
                          ? (
                              dataFilters[
                                checkOption.dataFilterField
                              ] as string[]
                            ).includes(field)
                          : false
                      }
                      onChange={() => {
                        const newFilteredData = handleCheckboxChange(
                          dataFilters,
                          field,
                          checkOption.dataFilterField as FilterableKeys,
                        );
                        setDataFilters(newFilteredData);
                      }}
                    />
                    <span className="pl-1">{field}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {/* SECOND ROW */}
          <div id="secondFilterRow" className="flex h-full w-full flex-col">
            <div className="flex pt-4"></div>
            <ul className="collapsible-list h-[calc(100%-30px)]">
              {secondCheckBoxOptions.map((checkOption, index) => (
                <li
                  key={index}
                  id="checkboxCluster"
                  className="cubeCustomAnalyticsScroll my-1 flex max-h-full w-[90%] flex-col overflow-auto overflow-x-hidden  pl-2 pr-2 pt-1"
                >
                  <div
                    className="item-header flex h-full w-full items-center justify-start py-1 pl-3"
                    // Add border here if wanting to show what sections have filtering on them.
                    onClick={() => toggleItem(index)}
                  >
                    <div>{expandedIndex === index ? "▽" : "▷"}</div>
                    <div className="pl-3">{checkOption.heading}</div>

                    {/* Indicator for expanded/collapsed */}
                  </div>
                  {expandedIndex === index && (
                    <div className="flex h-fit w-full flex-col pl-2">
                      {/* <div>{checkOption.heading}:</div> */}
                      <SelectAllCheckBox
                        dataFilters={dataFilters}
                        setDataFilters={setDataFilters}
                        dataFilterField={checkOption.dataFilterField}
                        keysToMap={keysToMap}
                        keysToMapField={checkOption.keysToMapField}
                      />
                      {keysToMap[checkOption.keysToMapField].map(
                        (field, index) => (
                          <div className=" text-xs" key={index}>
                            <input
                              type="checkbox"
                              className=" accent-blue-500"
                              checked={
                                dataFilters &&
                                dataFilters[checkOption.dataFilterField]
                                  ? (
                                      dataFilters[
                                        checkOption.dataFilterField
                                      ] as string[]
                                    ).includes(field)
                                  : false
                              }
                              onChange={() => {
                                const newFilteredData = handleCheckboxChange(
                                  dataFilters,
                                  field,
                                  checkOption.dataFilterField as FilterableKeys,
                                );
                                setDataFilters(newFilteredData);
                              }}
                            />
                            <span className="pl-1">{field}</span>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
};

export default MultiFilterSankey;
